import { USE_AUTO_TAGGING_MOCK } from "./autoTaggingService";

export interface RegulationAutoTagValue {
  code: string;
  name: string;
}

export interface RegulationAutoTagGroup {
  concept: string;
  concept_display_name: string;
  values: RegulationAutoTagValue[];
}

export type AutoTagValueRaw = {
  name?: string;
  code?: string;
  description?: string;
};

const MOCK_AUTO_TAG_GROUPS: RegulationAutoTagGroup[] = [
  {
    concept: "Country",
    concept_display_name: "Country",
    values: [
      { code: "DE", name: "Germany" },
      { code: "FR", name: "France" },
      { code: "US", name: "United States" },
      { code: "CN", name: "China" },
      { code: "JP", name: "Japan" },
      { code: "UK", name: "United Kingdom" },
      { code: "CA", name: "Canada" },
      { code: "AU", name: "Australia" },
      { code: "BR", name: "Brazil" },
      { code: "IN", name: "India" },
      { code: "KR", name: "South Korea" },
      { code: "IT", name: "Italy" },
      { code: "ES", name: "Spain" },
    ],
  },
  {
    concept: "Market",
    concept_display_name: "Market",
    values: [
      { code: "CN", name: "China" },
      { code: "US", name: "US" },
      { code: "AU", name: "Australia" },
      { code: "CU", name: "Cuba" },
      { code: "DM", name: "Dominica" },
      { code: "DO", name: "Dominican Republic" },
    ],
  },
  {
    concept: "Ingredient",
    concept_display_name: "Ingredient",
    values: [
      { code: "PEM", name: "Pembrolizumab" },
      { code: "ADA", name: "Adalimumab" },
      { code: "SEM", name: "Semaglutide" },
    ],
  },
  {
    concept: "Health Authority",
    concept_display_name: "Health Authority",
    values: [
      { code: "FDA", name: "FDA" },
      { code: "EMA", name: "EMA" },
      { code: "MHRA", name: "MHRA" },
      { code: "NMPA", name: "NMPA" },
      { code: "PMDA", name: "PMDA" },
    ],
  },
];

function normalizeAutoTagValue(raw: unknown): RegulationAutoTagValue | null {
  if (!raw || typeof raw !== "object") return null;
  const item = raw as AutoTagValueRaw;
  const name = typeof item.name === "string" ? item.name.trim() : "";
  if (!name) return null;
  const code =
    typeof item.code === "string" && item.code.trim()
      ? item.code.trim()
      : name;
  return { code, name };
}

function isAutoTagGroup(value: unknown): value is RegulationAutoTagGroup {
  if (!value || typeof value !== "object") return false;
  const group = value as RegulationAutoTagGroup;
  return (
    typeof group.concept === "string" &&
    Array.isArray(group.values) &&
    group.values.every(
      (item) =>
        item &&
        typeof item === "object" &&
        typeof item.name === "string",
    )
  );
}

function parseAutoTagsObject(
  raw: Record<string, unknown>,
): RegulationAutoTagGroup[] | null {
  const groups: RegulationAutoTagGroup[] = [];
  for (const [key, valuesRaw] of Object.entries(raw)) {
    const concept = key.trim();
    if (!concept || !Array.isArray(valuesRaw)) continue;
    const values = valuesRaw
      .map(normalizeAutoTagValue)
      .filter((item): item is RegulationAutoTagValue => Boolean(item));
    if (values.length === 0) continue;
    groups.push({
      concept,
      concept_display_name: concept,
      values,
    });
  }
  return groups.length > 0 ? groups : null;
}

/**
 * Parse a tag-groups payload shared by `auto_tags` and `merged_tags`:
 * - array of `{ concept, values }` groups
 * - object map `{ Concept: [{ name, code }] }`
 * - category/tag tree (same shape as manual `tags` / `categories`)
 */
export function parseTagGroupsPayload(
  raw: unknown,
): RegulationAutoTagGroup[] | null {
  if (raw == null) return null;

  if (Array.isArray(raw)) {
    if (raw.length === 0) return null;
    if (raw.every(isAutoTagGroup)) return raw;
    const fromTree = manualCategoriesToTagGroups(raw);
    return fromTree.length > 0 ? fromTree : null;
  }

  if (typeof raw === "object") {
    return parseAutoTagsObject(raw as Record<string, unknown>);
  }

  return null;
}

export function parseRegulationAutoTags(
  data: Record<string, unknown> | null | undefined,
): RegulationAutoTagGroup[] | null {
  return parseTagGroupsPayload(data?.auto_tags);
}

export function parseRegulationMergedTags(
  data: Record<string, unknown> | null | undefined,
): RegulationAutoTagGroup[] | null {
  return parseTagGroupsPayload(data?.merged_tags);
}

export function getMockRegulationAutoTags(): RegulationAutoTagGroup[] {
  return MOCK_AUTO_TAG_GROUPS.map((group) => ({
    ...group,
    values: group.values.map((value) => ({ ...value })),
  }));
}

export function resolveRegulationAutoTags(
  data: Record<string, unknown> | null | undefined,
): RegulationAutoTagGroup[] {
  const parsed = parseRegulationAutoTags(data);
  if (parsed) return parsed;
  if (USE_AUTO_TAGGING_MOCK) return getMockRegulationAutoTags();
  return [];
}

export function flattenAutoTagDisplayItems(
  groups: RegulationAutoTagGroup[],
): Array<{ name: string }> {
  return groups.flatMap((group) =>
    group.values.map((value) => ({ name: value.name })),
  );
}

/** Comma-joined names for list cell; empty string when no values. */
export function formatAutoTagsForListDisplay(
  groups: RegulationAutoTagGroup[] | null | undefined,
): string {
  if (!groups || groups.length === 0) return "";
  return flattenAutoTagDisplayItems(groups)
    .map((item) => item.name)
    .filter(Boolean)
    .join(", ");
}

/** API omitted `auto_tags` — empty TAG UI shows this. */
export const AUTO_TAG_ABSENT_PLACEHOLDER = "NA";

/**
 * `auto_tags` was returned but empty — e.g. `auto_tags: []` / `{}`,
 * or a concept under it is `[]`.
 */
export const AUTO_TAG_EMPTY_PLACEHOLDER = "-";

/** @deprecated Use AUTO_TAG_EMPTY_PLACEHOLDER — kept for call-site compatibility. */
export const AUTO_TAG_NEVER_RAN_PLACEHOLDER = AUTO_TAG_EMPTY_PLACEHOLDER;

/** @deprecated Use AUTO_TAG_ABSENT_PLACEHOLDER — kept for call-site compatibility. */
export const AUTO_TAG_NO_RESULTS_PLACEHOLDER = AUTO_TAG_ABSENT_PLACEHOLDER;

/**
 * Empty-state copy for Auto Tags and dynamic concept columns (list + detail).
 * - API omitted `auto_tags` → "NA"
 * - `auto_tags` returned empty → "-"
 */
export function autoTagEmptyDisplay(fieldPresent: boolean): string {
  return fieldPresent
    ? AUTO_TAG_EMPTY_PLACEHOLDER
    : AUTO_TAG_ABSENT_PLACEHOLDER;
}

export function getAutoTagGroupLabel(group: RegulationAutoTagGroup): string {
  return (
    group.concept_display_name?.trim() || group.concept?.trim() || "Concept"
  );
}

export function sortAutoTagGroupsByLabel(
  groups: RegulationAutoTagGroup[],
): RegulationAutoTagGroup[] {
  return [...groups].sort((a, b) =>
    getAutoTagGroupLabel(a).localeCompare(getAutoTagGroupLabel(b), undefined, {
      sensitivity: "base",
    }),
  );
}

export function countTagValues(groups: RegulationAutoTagGroup[]): number {
  return flattenAutoTagDisplayItems(groups).length;
}

/**
 * Pill count for detail and home: merged/auto groups plus manual category
 * leaves whose names are not already in those groups.
 */
export function countDisplayedTags(
  groups: readonly RegulationAutoTagGroup[],
  categoryItems?: readonly unknown[] | null,
): number {
  const base = countTagValues([...groups]);
  const manual = manualCategoriesToTagGroups(categoryItems);
  if (manual.length === 0) return base;

  const seen = new Set<string>();
  for (const group of groups) {
    for (const value of group.values) {
      const name = value.name.trim().toLowerCase();
      if (name) seen.add(name);
    }
  }

  let extra = 0;
  for (const group of manual) {
    for (const value of group.values) {
      const name = value.name.trim().toLowerCase();
      if (!name || seen.has(name)) continue;
      seen.add(name);
      extra += 1;
    }
  }
  return base + extra;
}

/** Manual tags tree node (categories / tags). */
export type ManualTagCategoryNode = {
  name?: string;
  code?: string;
  id?: string;
  is_deleted?: boolean;
  children?: ManualTagCategoryNode[];
};

function collectManualTagLeafValues(
  node: ManualTagCategoryNode,
): RegulationAutoTagValue[] {
  if (node.is_deleted === true) return [];
  const children = Array.isArray(node.children) ? node.children : [];
  const visibleChildren = children.filter((child) => child?.is_deleted !== true);
  if (visibleChildren.length === 0) {
    const name = typeof node.name === "string" ? node.name.trim() : "";
    if (!name || name === "All") return [];
    const code =
      typeof node.code === "string" && node.code.trim()
        ? node.code.trim()
        : name;
    return [{ code, name }];
  }
  return visibleChildren.flatMap((child) => collectManualTagLeafValues(child));
}

/**
 * Convert manual categories/tags tree into concept groups for Tags Details.
 * Top-level nodes become group headers; leaf names become tag values.
 */
export function manualCategoriesToTagGroups(
  categories: unknown,
): RegulationAutoTagGroup[] {
  if (!Array.isArray(categories) || categories.length === 0) return [];

  const groups: RegulationAutoTagGroup[] = [];
  for (const raw of categories) {
    if (!raw || typeof raw !== "object") continue;
    const node = raw as ManualTagCategoryNode;
    if (node.is_deleted === true) continue;
    const concept =
      typeof node.name === "string" && node.name.trim()
        ? node.name.trim()
        : "";
    if (!concept) continue;

    const leafValues = collectManualTagLeafValues(node);
    const uniqueLeaves: RegulationAutoTagValue[] = [];
    const leafSeen = new Set<string>();
    for (const value of leafValues) {
      const key = `${value.code}::${value.name}`;
      if (leafSeen.has(key)) continue;
      leafSeen.add(key);
      uniqueLeaves.push(value);
    }

    if (uniqueLeaves.length === 0) continue;
    groups.push({
      concept,
      concept_display_name: concept,
      values: uniqueLeaves,
    });
  }

  return groups;
}

/**
 * Display groups for Tags column / Tags Details modal.
 * Prefer backend `merged_tags` when present; otherwise fall back to AC7:
 * - Auto tags present with values → use auto tags (covers/replaces manual).
 * - Auto tags field present but empty → show none (do not fall back to manual).
 * - Auto tags absent → show manual categories/tags.
 */
export function resolveDisplayTagGroups(
  data: Record<string, unknown> | null | undefined,
): RegulationAutoTagGroup[] {
  if (!data) return [];

  if (Object.prototype.hasOwnProperty.call(data, "merged_tags")) {
    const parsedMerged = parseRegulationMergedTags(data);
    return sortAutoTagGroupsByLabel(parsedMerged ?? []);
  }

  const autoPresent = Object.prototype.hasOwnProperty.call(data, "auto_tags");
  const parsedAuto = parseRegulationAutoTags(data);
  if (parsedAuto && parsedAuto.length > 0) {
    return sortAutoTagGroupsByLabel(parsedAuto);
  }
  if (autoPresent) {
    return [];
  }

  const manual = data.categories ?? data.tags;
  return sortAutoTagGroupsByLabel(manualCategoriesToTagGroups(manual));
}
